import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, createSignal, Show } from "solid-js";
import { useI18n } from "~/i18n/context";

interface DeleteMemoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  memoryContent: string;
}

export const DeleteMemoryModal: Component<DeleteMemoryModalProps> = (props) => {
  const { t } = useI18n();
  const [loading, setLoading] = createSignal(false);
  const [deleteError, setDeleteError] = createSignal<string | null>(null);

  const handleDelete = async () => {
    if (loading()) return;
    setDeleteError(null);
    setLoading(true);
    try {
      await props.onConfirm();
      props.onClose();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const truncatedContent = () => {
    const content = props.memoryContent;
    return content.length > 120 ? `${content.slice(0, 120)}...` : content;
  };

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <div class="mb-6">
                  <h3 class="mb-4 text-lg font-medium">{t("memory.delete.modal_title")}</h3>
                  <p class="mb-3 text-sm text-secondary-text">{t("memory.delete.modal_description")}</p>
                  <div class="rounded-lg border border-muted-border bg-canvas p-3">
                    <p class="text-sm text-muted-text italic">{truncatedContent()}</p>
                  </div>
                </div>

                <Show when={deleteError()}>
                  <div class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20">
                    <p class="text-sm text-red-700 dark:text-red-300">{deleteError()}</p>
                  </div>
                </Show>

                <div class="flex items-center justify-end gap-3">
                  <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                    {t("common.cancel")}
                  </StButton>
                  <StButton
                    onClick={() => void handleDelete()}
                    loading={loading()}
                    disabled={loading()}
                    class="bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                  >
                    {t("memory.delete.button")}
                  </StButton>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
